import { DebugLog } from "../debug/log";
import type { BuildSpec, Idea, Settings } from "../data/types";
import type { ModelInfo } from "../data/modelStore";
import { activeEngineLabel, getActiveEngine } from "./active";
import { BUILD_SPEC_SYSTEM_PROMPT, buildSpecUserMessage } from "./prompts";

const TAG = "engine.buildSpec";

export type BuildSpecOutcome =
  | { kind: "success"; spec: BuildSpec }
  | { kind: "error"; message: string };

export async function generateBuildSpec(
  idea: Idea,
  settings: Settings,
  installedModel: ModelInfo | null,
): Promise<BuildSpecOutcome> {
  const engine = getActiveEngine(settings, installedModel);
  if (!engine) return { kind: "error", message: "No engine configured." };
  DebugLog.i(TAG, `build spec starting for idea ${idea.id}`);
  const result = await engine.complete({
    messages: [
      { role: "system", content: BUILD_SPEC_SYSTEM_PROMPT },
      { role: "user", content: buildSpecUserMessage(idea) },
    ],
    maxTokens: 4096,
    temperature: 0.4,
  });
  if (result.kind === "error") return { kind: "error", message: result.message };
  const markdown = stripCodeFenceWrapping(result.text).trim();
  if (markdown.length === 0) {
    return { kind: "error", message: "Engine returned an empty spec." };
  }
  const productName = extractProductName(markdown, idea.title);
  return {
    kind: "success",
    spec: {
      generatedAtMs: Date.now(),
      productNameSuggestion: productName,
      markdown,
      source: activeEngineLabel(settings, installedModel),
    },
  };
}

function stripCodeFenceWrapping(raw: string): string {
  const trimmed = raw.trim();
  const fenced = trimmed.match(/^```(?:markdown|md)?\s*\n([\s\S]*?)\n```\s*$/);
  return fenced ? fenced[1] : trimmed;
}

function extractProductName(markdown: string, fallback: string): string {
  const firstHeading = markdown.match(/^\s*#\s+(.+?)\s*$/m);
  if (firstHeading) return firstHeading[1].trim();
  return fallback;
}
